import { useEffect, useRef } from 'react';
import { tracker } from '../track';
const TRACK_INTERVAL_MS = 10000;
const TRACK_IMPRESSION_LIMIT = 15;
const TRACKING_QUERY = '[id^="open-Z"],[id^="thb-Z"][aria-hidden="false"],[id^="upcoming-Z"]';
const isRectIntersecting = (intersectionRect) => intersectionRect.height > 0 && intersectionRect.width > 0;
export const useTrackCampaignsImpression = ({ categoryTabId, isMobileScreen, visitorID, }) => {
    const impressionsRef = useRef([]); // use ref to persist data and avoid rerender
    const getCampaignImpressionData = (impressions) => {
        const trackingData = {
            ecommerce: {
                promoView: {
                    promotions: [...impressions],
                },
            },
        };
        return trackingData;
    };
    const getTrackingObject = (element, categoryTabId, visitorID) => {
        const elementbyId = element.getAttribute('id');
        if (elementbyId) {
            const elementIdArray = elementbyId.split('-');
            const source = elementIdArray[0];
            const id = elementIdArray[1];
            const isGetInspired = elementIdArray.length === 3 && elementIdArray[2] === 'isGetInspired';
            return {
                id,
                name: source,
                position: element.getAttribute('data-position'),
                creative: `${categoryTabId}|campaignOverview`,
                visitorID,
                dimension113: isGetInspired ? '1' : '0',
            };
        }
        return {};
    };
    useEffect(() => {
        const trackingCallback = () => {
            if (impressionsRef.current.length) {
                tracker.track('view_item_list', getCampaignImpressionData(impressionsRef.current));
                impressionsRef.current = [];
            }
        };
        // Create IntersectionObserver to track campaign impressions
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting && isRectIntersecting(entry.intersectionRect)) {
                impressionsRef.current = [
                    ...impressionsRef.current,
                    getTrackingObject(entry.target, categoryTabId, visitorID),
                ];
                observer.unobserve(entry.target);
            }
            if (impressionsRef.current.length === TRACK_IMPRESSION_LIMIT) {
                trackingCallback();
            }
        }), {
            root: null,
            rootMargin: '0px',
            threshold: 1.0, // trigger if 100% in viewport
        });
        // throttle impression sending
        const intervalId = setInterval(trackingCallback, TRACK_INTERVAL_MS);
        window.addEventListener('beforeunload', trackingCallback);
        // give time to animation to complete
        const animateTimeoutId = setTimeout(() => {
            const campaignElements = document.querySelectorAll(TRACKING_QUERY);
            // start observing nodes
            Array.from(campaignElements).forEach(el => observer.observe(el));
        }, 600);
        // upcoming section now is lazy-loadable, so track lazy loading we use Mutation Observer and add them to Intersection Obs. list
        let upcomingAnimateTimeoutId;
        const config = {
            childList: true,
            subtree: true,
        };
        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mut => {
                Array.from(mut.addedNodes).forEach((value) => {
                    // after upcoming section is added, we need to start observing campaigns inside
                    const node = value.firstChild;
                    if (node) {
                        if (node.id === 'upcoming-campaigns') {
                            upcomingAnimateTimeoutId = setTimeout(() => {
                                const upcomingCampaignElements = node.querySelectorAll(TRACKING_QUERY);
                                // start observing nodes
                                Array.from(upcomingCampaignElements).forEach(el => observer.observe(el));
                            }, 100);
                        }
                        // track lazy loaded open campaigns on mobile
                        if (node.id && node.id.startsWith('open-Z')) {
                            // console.log('add tracking for node', node.id);
                            observer.observe(node);
                        }
                    }
                });
            });
        });
        // give animations a little bit time, might be changed with RAF
        const mutationObserverTimeoutId = setTimeout(() => {
            // in mobile we use lazy load for campaigns
            const openCampaignQuerySelector = document.querySelector('#campaigns-open');
            if (isMobileScreen && openCampaignQuerySelector) {
                mutationObserver.observe(openCampaignQuerySelector, config);
            }
            const upcomingCampaignsQuerySelector = document.querySelector('#lazyload-campaigns-upcoming');
            if (upcomingCampaignsQuerySelector) {
                mutationObserver.observe(upcomingCampaignsQuerySelector, config);
            }
        }, 500);
        return () => {
            if (upcomingAnimateTimeoutId) {
                clearTimeout(upcomingAnimateTimeoutId);
            }
            clearTimeout(animateTimeoutId);
            clearInterval(intervalId);
            observer.disconnect();
            window.removeEventListener('beforeunload', trackingCallback);
            if (mutationObserver) {
                mutationObserver.disconnect();
                clearTimeout(mutationObserverTimeoutId);
            }
        };
    }, [visitorID, categoryTabId, isMobileScreen, getTrackingObject]);
};
